import React from 'react';
import PropTypes from 'prop-types';

import {Alert, Box, CircularProgress, Stack} from 'views/components';
import {translate} from 'views/module';

import InvitationCard from './InvitationCard';
import InvitationCancelDialog from './InvitationCancelDialog';

import backend from 'utils/backend';

// The caregiver's job invitations, newest first. Cancel on a waiting invitation is the one action
// on the Loom caregiver page.
const LoomCaregiverInvitations = (props) => {
  const {caregiverId, caregiverName, refresh} = props;

  const [invitations, setInvitations] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [reload, setReload] = React.useState(0);
  const [cancelTarget, setCancelTarget] = React.useState(null);

  React.useEffect(() => {
    let isCancelled = false;

    const loadInvitations = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await backend.loom.caregiver.listInvitations(caregiverId);
        if (!isCancelled) setInvitations(result);
      } catch (loadError) {
        console.error(loadError);
        if (isCancelled) return;
        setInvitations([]);
        setError(translate('caregiver.LoomCaregiverDetail.Invitations.LoadError'));
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    loadInvitations();

    return () => {
      isCancelled = true;
    };
  }, [caregiverId, refresh, reload]);

  const onCancelled = React.useCallback(() => {
    setCancelTarget(null);
    setReload((value) => value + 1);
  }, []);

  if (loading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 3}}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2} sx={{p: 2}}>
      {
        error &&
        <Alert severity='error'>{error}</Alert>
      }
      {
        !error && invitations.length === 0 &&
        <Alert severity='info'>{translate('caregiver.LoomCaregiverDetail.Invitations.Empty')}</Alert>
      }
      {
        invitations.map((invitation) => (
          <InvitationCard
            key={invitation.id}
            invitation={invitation}
            onCancel={setCancelTarget}
          />
        ))
      }
      {
        cancelTarget &&
        <InvitationCancelDialog
          invitationId={cancelTarget.id}
          caregiverName={caregiverName}
          onClose={() => setCancelTarget(null)}
          onCancelled={onCancelled}
        />
      }
    </Stack>
  );
};
LoomCaregiverInvitations.propTypes = {
  caregiverId: PropTypes.string.isRequired,
  caregiverName: PropTypes.string,
  refresh: PropTypes.number.isRequired,
};

export default React.memo(LoomCaregiverInvitations);
